// src/pages/ProductData.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Loader, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import Navbar from '../components/navbar';
import { getUserRoleFromToken } from '../hydraApiService';

function ProductData() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [showSpinner, setShowSpinner] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    hasMore: false
  });

  // Batch DPP creation state
  const [creatingDPP, setCreatingDPP] = useState(false);
  const [dppSuccess, setDppSuccess] = useState(false);
  const [dppError, setDppError] = useState(null);

  // Role
  const userRole = getUserRoleFromToken();
  const isManufacturer = userRole === 'Manufacturer';

  // Get size filter from URL
  const searchParams = new URLSearchParams(location.search);
  const sizeFilter = searchParams.get('size');

  const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

  // Batch Create DPP
  const handleCreateDPPBatch = async () => {
    try {
      setCreatingDPP(true);
      setDppError(null);
      setDppSuccess(false);

      const response = await fetch(`${API_BASE_URL}/createproduct/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });

      if (!response.ok) throw new Error(`Failed to create DPPs: ${response.status}`);

      const data = await response.json();
      console.log('Batch DPP Creation Response:', data);

      setDppSuccess(true);
      setTimeout(() => {
        fetchProducts(currentPage);
        setDppSuccess(false);
      }, 1500);

    } catch (err) {
      console.error('Error creating DPPs:', err);
      setDppError(err.message);
      setTimeout(() => setDppError(null), 5000);
    } finally {
      setCreatingDPP(false);
    }
  };

  // Fetch products
  const fetchProducts = async (page = 1) => {
    let spinnerTimer = null;
    try {
      setLoading(true);
      setError(null);

      spinnerTimer = setTimeout(() => setShowSpinner(true), 900);

      const offset = (page - 1) * itemsPerPage;
      const url = sizeFilter
        ? `${API_BASE_URL}/products-by-size/${sizeFilter}?limit=${itemsPerPage}&offset=${offset}`
        : `${API_BASE_URL}/products?limit=${itemsPerPage}&offset=${offset}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) throw new Error(`Failed to fetch products: ${response.status}`);

      const data = await response.json();
      console.log('API Response:', data);

      let productList = [];
      let paginationData = null;

      if (data.data && data.data.products && Array.isArray(data.data.products)) {
        productList = data.data.products;
        paginationData = data.data.pagination;
      } else if (data.products && Array.isArray(data.products)) {
        productList = data.products;
        paginationData = data.pagination;
      } else if (Array.isArray(data)) {
        productList = data;
      } else if (data.data && Array.isArray(data.data)) {
        productList = data.data;
      } else {
        console.error('Unexpected API response structure:', data);
        throw new Error('Invalid response format. Check console for details.');
      }

      setProducts(productList);

      if (paginationData) {
        setPagination({
          total: paginationData.total || productList.length,
          totalPages: paginationData.totalPages || 1,
          hasMore: paginationData.hasMore || false
        });
      } else {
        setPagination({ total: productList.length, totalPages: 1, hasMore: false });
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Error fetching products:', err);
      let errorMessage = err.message;
      if (err.message.includes('Failed to fetch') || err.message.includes('Network')) {
        errorMessage = 'Network error. Please check your internet connection or if the API server is running.';
      } else if (err.message.includes('CORS')) {
        errorMessage = 'CORS error. The API server may need to allow requests from this domain.';
      }
      setError(errorMessage);
    } finally {
      if (spinnerTimer) clearTimeout(spinnerTimer);
      setShowSpinner(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage);
  }, [currentPage, sizeFilter]);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) setCurrentPage(currentPage + 1);
  };

  const handleRetry = () => {
    setError(null);
    setCurrentPage(1);
    fetchProducts(1);
  };

  const getShowingRange = () => {
    const start = (currentPage - 1) * itemsPerPage + 1;
    const end = Math.min(currentPage * itemsPerPage, pagination.total);
    return { start, end };
  };

  // ── Product Card — Mobile ────────────────────────────────────────────────────
  const ProductCard = ({ product }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center border border-gray-300">
          <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <line x1="8" y1="21" x2="16" y2="21" strokeWidth="2" strokeLinecap="round"/>
            <line x1="12" y1="17" x2="12" y2="21" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => product.hasDpp && navigate(`/product-passport/${product.dppId}`)}
            disabled={!product.hasDpp}
            className={`px-3 py-1.5 text-xs rounded flex items-center gap-1 transition-colors ${
              product.hasDpp
                ? 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            <FileText className="w-3 h-3" />
            Passport
          </button>
          <button
            onClick={() => product.hasDpp && navigate(`/tracker/${product.dppId}`)}
            disabled={!product.hasDpp}
            className={`px-3 py-1.5 text-xs rounded transition-colors ${
              product.hasDpp
                ? 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Track
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <p className="text-xs text-gray-500 mb-0.5">DPP ID</p>
          <p className="text-sm text-gray-700">{product.dppId || 'Not Generated'}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Serial Number</p>
          <p className="text-sm text-gray-700">{product.serialNumber || product.uniqueProductIdentifier}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Product Name</p>
          <p className="text-sm font-medium text-gray-900">{product.productName || 'N/A'}</p>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Brand</p>
            <p className="text-sm text-gray-700">{product.brand || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Category</p>
            <p className="text-sm text-gray-700">{product.productCategory || 'N/A'}</p>
          </div>
        </div>
        <div>
          <p className="text-xs text-gray-500 mb-0.5">Manufacturer</p>
          <p className="text-sm text-gray-700">{product.manufacturerName || 'N/A'}</p>
        </div>
      </div>
    </div>
  );

  // ── Pagination ───────────────────────────────────────────────────────────────
  const PaginationControls = () => {
    const { start, end } = getShowingRange();
    return (
      <div className="bg-white border-t border-gray-200 px-4 py-3 sm:px-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">{start}</span> to{' '}
            <span className="font-medium">{end}</span> of{' '}
            <span className="font-medium">{pagination.total}</span> products
            {sizeFilter && <span> ({sizeFilter} inches)</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                currentPage === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </button>
            <div className="px-3 py-1.5 text-sm font-medium text-gray-700">
              Page {currentPage} of {pagination.totalPages}
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage === pagination.totalPages}
              className={`px-3 py-1.5 rounded-lg flex items-center gap-1 text-sm font-medium transition-colors ${
                currentPage === pagination.totalPages
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Loading state ────────────────────────────────────────────────────────────
  if (showSpinner && currentPage === 1) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading products...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Products</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* ── Header Bar ── */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl shadow">
        <button
          onClick={() => navigate('/master-data')}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base font-medium"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          Back
        </button>

        <div className="flex items-center gap-2 sm:gap-3">
          {isManufacturer && (
            <button
              onClick={handleCreateDPPBatch}
              disabled={creatingDPP}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 min-w-[110px] justify-center ${
                creatingDPP
                  ? 'bg-blue-400 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {creatingDPP ? (
                <><Loader className="w-4 h-4 animate-spin" />Creating...</>
              ) : (
                'Create DPP'
              )}
            </button>
          )}
          {isManufacturer && (
            <button
              onClick={() => navigate('/role-based-access')}
              className="px-5 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors min-w-[110px] text-center"
            >
              RBR
            </button>
          )}
          <button
            onClick={() => navigate('/product-data')}
            className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors whitespace-nowrap"
          >
            Product Data
          </button>
        </div>
      </div>

      {/* Success / Error toasts */}
      {dppSuccess && (
        <div className="mx-2 sm:mx-4 mt-3 bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">DPPs created successfully! Refreshing product list...</p>
        </div>
      )}
      {dppError && (
        <div className="mx-2 sm:mx-4 mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-800">{dppError}</p>
        </div>
      )}

      {/* Loading overlay for pagination */}
      {showSpinner && currentPage > 1 && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 flex items-center gap-3">
            <Loader className="w-6 h-6 text-blue-600 animate-spin" />
            <p className="text-gray-700">Loading page {currentPage}...</p>
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <div className="p-4 sm:p-6 lg:p-4">

        {/* ── Desktop Table ── */}
        <div className="hidden lg:block bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">DPP ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Serial Number</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Product Name</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Brand</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Manufacturer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr
                    key={`${product.serialNumber || product.uniqueProductIdentifier}-${product.dppId || index}`}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-4 py-4 text-sm text-gray-700">{product.dppId || 'Not Generated'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{product.serialNumber || product.uniqueProductIdentifier}</td>
                    <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate">{product.productName || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{product.brand || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{product.manufacturerName || 'N/A'}</td>
                    <td className="px-4 py-4 text-sm text-gray-700">{product.productCategory || 'N/A'}</td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => product.hasDpp && navigate(`/product-passport/${product.dppId}`)}
                          disabled={!product.hasDpp}
                          className={`px-3 py-1 text-sm rounded transition-colors whitespace-nowrap ${
                            product.hasDpp
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          View Passport
                        </button>
                        <button
                          onClick={() => product.hasDpp && navigate(`/tracker/${product.dppId}`)}
                          disabled={!product.hasDpp}
                          className={`px-3 py-1 text-sm rounded transition-colors whitespace-nowrap ${
                            product.hasDpp
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          Track
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && <PaginationControls />}
        </div>

        {/* ── Tablet Table ── */}
        <div className="hidden md:block lg:hidden bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">DPP ID</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Product Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Brand</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, index) => (
                  <tr
                    key={`${product.serialNumber || product.uniqueProductIdentifier}-${product.dppId || index}`}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-3 py-3 text-xs text-gray-700">{product.dppId || 'Not Generated'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700 max-w-[200px] truncate">{product.productName || 'N/A'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{product.brand || 'N/A'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{product.productCategory || 'N/A'}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => product.hasDpp && navigate(`/product-passport/${product.dppId}`)}
                          disabled={!product.hasDpp}
                          className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap ${
                            product.hasDpp
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          Passport
                        </button>
                        <button
                          onClick={() => product.hasDpp && navigate(`/tracker/${product.dppId}`)}
                          disabled={!product.hasDpp}
                          className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap ${
                            product.hasDpp
                              ? 'bg-green-600 text-white hover:bg-green-700'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          Track
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-gray-50 text-xs text-gray-600 text-center border-t">
            Scroll horizontally to see more columns →
          </div>
          {pagination.totalPages > 1 && <PaginationControls />}
        </div>

        {/* ── Mobile Card View ── */}
        <div className="md:hidden">
          {products.map(product => (
            <ProductCard key={product.dppId} product={product} />
          ))}
          {pagination.totalPages > 1 && (
            <div className="bg-white rounded-lg shadow-sm mt-4">
              <PaginationControls />
            </div>
          )}
        </div>

        {/* ── Empty State ── */}
        {products.length === 0 && !loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">
              There are currently no products{sizeFilter && ` for size ${sizeFilter}"`} in the system.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductData;